import React, { useState, useEffect } from "react";
import { useI18n } from '../context/I18nContext';
import api from "../services/api";

const StatCard = ({ label, value, sublabel, color = "blue", icon }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-100`}>
        <span className={`text-2xl text-${color}-600`}>{icon}</span>
      </div>
    </div>
    <p className="text-3xl font-bold text-gray-900">{value}</p>
    <p className="text-sm font-medium text-gray-500 mt-1">{label}</p>
    {sublabel && <p className="text-xs text-gray-400 mt-0.5">{sublabel}</p>}
  </div>
);

const RateBar = ({ label, value, color }) => (
  <div className="flex items-center gap-4">
    <span className="text-sm text-gray-600 w-24">{label}</span>
    <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full bg-${color}-500 transition-all duration-500`}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
    <span className="text-sm font-semibold text-gray-700 w-14 text-right">{value}%</span>
  </div>
);

const DashboardHome = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/whatsapp/history?limit=10")
      ]);
      setStats(statsRes.data);
      setRecentMessages(historyRes.data.messages || []);
    } catch (error) {
      console.error(t('dashboardh.a1'), error);
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: "bg-blue-100 text-blue-700",
      delivered: "bg-green-100 text-green-700",
      read: "bg-emerald-100 text-emerald-700",
      failed: "bg-red-100 text-red-700"
    };
    const labels = {
      sent: "Enviada",
      delivered: "Entregue",
      read: "Lida",
      failed: "Falhou"
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || "bg-gray-100 text-gray-700"}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-whatsapp"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('dash.title')}</h1>
        <p className="text-gray-500 mt-1">{t('dash.overviewFull')}</p>
      </div>

      {/* Cards de Metricas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={"\uD83D\uDCE8"}
          label={t('dashboardh.a2')}
          value={stats?.messages?.today || 0}
          sublabel={t('dashboardh.a3', { v1: stats?.messages?.thisWeek || 0 })}
          color="blue"
        />
        <StatCard
          icon={"\uD83D\uDCCA"}
          label={t('dashboardh.a4')}
          value={stats?.messages?.total || 0}
          sublabel={t('dashboardh.a5', { v1: stats?.messages?.thisMonth || 0 })}
          color="indigo"
        />
        <StatCard
          icon={"\uD83D\uDD17"}
          label={t('dashboardh.a6')}
          value={`${stats?.connections?.active || 0}/${stats?.connections?.total || 0}`}
          color="green"
        />
        <StatCard
          icon={"\uD83D\uDC65"}
          label={t('dashboardh.a7')}
          value={stats?.users?.total || 0}
          color="purple"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Taxas de Entrega */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">{t('dash.deliveryRates')}</h2>
          <div className="space-y-5">
            <RateBar label="Entrega" value={stats?.rates?.delivery || 0} color="green" />
            <RateBar label="Leitura" value={stats?.rates?.read || 0} color="emerald" />
            <RateBar label={t('dashboardh.a8')} value={stats?.rates?.failure || 0} color="red" />
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 mb-3">{t('dash.byStatus')}</h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "Enviadas", value: stats?.messages?.byStatus?.sent || 0, color: "blue" },
                { label: "Entregues", value: stats?.messages?.byStatus?.delivered || 0, color: "green" },
                { label: "Lidas", value: stats?.messages?.byStatus?.read || 0, color: "emerald" },
                { label: "Falhas", value: stats?.messages?.byStatus?.failed || 0, color: "red" }
              ].map(s => (
                <div key={s.label} className="text-center">
                  <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
                  <p className="text-xs text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mensagens Recentes */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{t('dash.recent')}</h2>
            <a href="/dashboard/history" className="text-sm text-whatsapp hover:underline">{t('dash.seeAll')}</a>
          </div>
          <div className="divide-y divide-gray-100">
            {recentMessages.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>{t('dash.noMessages')}</p>
                <p className="text-sm mt-1">{t('dash.sendFirst')}</p>
              </div>
            ) : (
              recentMessages.map(msg => (
                <div key={msg.id} className="px-6 py-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">{msg.recipientPhone}</p>
                        {getStatusBadge(msg.status)}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Template: {msg.templateName} &middot; {new Date(msg.createdAt).toLocaleString("pt-BR")}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
